"use client";

import React, { useState, useCallback, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

type Format = "number" | "hours" | "currency" | "text" | "date";

interface InlineEditableValueProps {
  field: string;
  value: number | string | null | undefined;
  onSave: (field: string, value: number | string | null) => Promise<void>;
  format?: Format;
  placeholder?: string;
  className?: string;
  "data-testid"?: string;
}

function formatDisplay(value: number | string | null | undefined, format: Format): string {
  if (value === null || value === undefined || value === "") {
    return format === "hours" ? "0 hours" : format === "currency" ? "0.00" : "—";
  }
  const num = typeof value === "string" ? parseFloat(value) : value;
  switch (format) {
    case "hours":
      return `${Number.isNaN(num) ? 0 : num} hours`;
    case "currency":
      return `$${Number.isNaN(num) ? "0.00" : (num).toFixed(2)}`;
    case "number":
      return Number.isNaN(num) ? String(value) : String(num);
    case "date":
      return value ? new Date(value).toLocaleDateString() : "—";
    default:
      return String(value);
  }
}

function parseInput(input: string, format: Format): number | string | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;
  if (format === "number" || format === "hours" || format === "currency") {
    const n = parseFloat(trimmed.replace(/[$,]/g, ""));
    return Number.isNaN(n) ? null : n;
  }
  if (format === "date") {
    return trimmed || null;
  }
  return trimmed;
}

export function InlineEditableValue({
  field,
  value,
  onSave,
  format = "text",
  placeholder = "—",
  className,
  "data-testid": dataTestId,
}: InlineEditableValueProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const clickingButtonRef = useRef(false);

  const displayValue = formatDisplay(value, format);

  useEffect(() => {
    if (isEditing) {
      const raw =
        format === "currency" && typeof value === "number"
          ? String(value)
          : value ?? "";
      setInputValue(String(raw));
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing, format, value]);

  const handleCancel = useCallback(() => {
    setInputValue(String(value ?? ""));
    setIsEditing(false);
  }, [value]);

  const handleSave = useCallback(async () => {
    const parsed = parseInput(inputValue, format);
    const same =
      (format === "hours" || format === "number" || format === "currency"
        ? (value ?? 0) === (parsed ?? 0) || (Number(value) === Number(parsed) && value !== "")
        : String(value ?? "") === String(parsed ?? ""));
    if (same) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(field, parsed);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  }, [field, inputValue, format, value, onSave]);

  const handleBlur = useCallback(() => {
    if (clickingButtonRef.current) return;
    void handleSave();
  }, [handleSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void handleSave();
      }
      if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  if (isEditing) {
    return (
      <div
        className={cn(
          "mt-1 flex items-center gap-2 p-2 rounded-lg border border-blue-200 bg-linear-to-r from-blue-50 to-indigo-50 shadow-sm ring-1 ring-blue-100",
          className
        )}
      >
        <Input
          ref={inputRef}
          type={format === "date" ? "date" : "text"}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={saving}
          className="min-w-[100px] h-8 text-sm border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 bg-card shadow-sm flex-1"
          data-testid={dataTestId}
        />
        <div className="flex items-center gap-1 shrink-0">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 hover:bg-green-100 text-green-600 hover:text-green-700 transition-all duration-200 hover:scale-105 dark:hover:bg-green-900/40 dark:text-green-400 dark:hover:text-green-300"
            title="Accept (Enter)"
            disabled={saving}
            onMouseDown={(e) => {
              e.preventDefault();
              clickingButtonRef.current = true;
            }}
            onClick={async () => {
              clickingButtonRef.current = false;
              await handleSave();
            }}
          >
            <CheckCircle className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 hover:bg-red-100 text-red-600 hover:text-red-700 transition-all duration-200 hover:scale-105 dark:hover:bg-red-900/40 dark:text-red-400 dark:hover:text-red-300"
            title="Cancel (Escape)"
            disabled={saving}
            onMouseDown={(e) => {
              e.preventDefault();
              clickingButtonRef.current = true;
            }}
            onClick={() => {
              clickingButtonRef.current = false;
              handleCancel();
            }}
          >
            <XCircle className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setIsEditing(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsEditing(true);
        }
      }}
      className={cn(
        "mt-1 text-sm text-card-foreground min-h-8 flex items-center rounded px-2 -mx-2 hover:bg-muted/60 cursor-pointer border border-transparent hover:border-input transition-colors",
        className
      )}
      data-testid={dataTestId}
      title="Click to edit"
    >
      {displayValue}
    </div>
  );
}
